'use client';
import { ProductDocument, ProductLight } from '@models/product';
import { formatCurrency, cn } from '@util/index';

interface ProductPriceRangeProps {
  product: ProductDocument | ProductLight;
  className?: string;
  showSalePrice?: boolean;
  textColor?: 'white' | 'default';
  textSize?: 'default' | 'large';
}

const ProductPriceRange = ({
  product,
  className = '',
  showSalePrice = true,
  textColor = 'default',
  textSize = 'default',
}: ProductPriceRangeProps) => {
  const fullPrice = product.price;

  const variationPriceRange = () => {
    if ('variations' in product && Array.isArray(product.variations)) {
      const isGearCombo =
        product.category2 === 'Gear Combos' &&
        product.variations.some((pv) => pv.is_number) &&
        product.variations.some((pv) => !pv.is_number);

      if (isGearCombo) {
        const numberSizes = product.variations.filter((pv) => pv.is_number);
        const letterSizes = product.variations.filter((pv) => !pv.is_number);

        const numberPrices = numberSizes.map((ns) => ns.price);
        const letterPrices = letterSizes.map((ls) => ls.price);

        const minNumberSizePrice = Math.min(...numberPrices);
        const maxNumberSizePrice = Math.max(...numberPrices);

        const minLetterSizePrice = Math.min(...letterPrices);
        const maxLetterSizePrice = Math.max(...letterPrices);

        const min = minNumberSizePrice + minLetterSizePrice;
        const max = maxNumberSizePrice + maxLetterSizePrice;
        if (min === max) return;
        return {
          minPrice: minNumberSizePrice + minLetterSizePrice,
          maxPrice: maxNumberSizePrice + maxLetterSizePrice,
        };
      } else {
        const filteredPrices = product.variations
          ?.filter((v) => v.price && v.qty > 0)
          .map((v) => v.price);

        if (!filteredPrices?.length) return;

        const minPrice = Math.min(...filteredPrices);
        const maxPrice = Math.max(...filteredPrices);

        if (minPrice === maxPrice) return;

        return { minPrice, maxPrice };
      }
    }

    return;
  };

  const { minPrice, maxPrice } = variationPriceRange() ?? {};

  const checkIfIsLightProduct = (
    product: ProductDocument | ProductLight
  ): product is ProductLight => {
    return (
      'minPrice' in product &&
      typeof product.minPrice === 'number' &&
      'maxPrice' in product &&
      typeof product.maxPrice === 'number'
    );
  };

  const isLightProduct = checkIfIsLightProduct(product);

  if ((minPrice && maxPrice) || isLightProduct) {
    const productWithRange = isLightProduct ? product : null;

    const displayMin =
      minPrice && maxPrice ? minPrice : productWithRange?.minPrice;
    const displayMax =
      minPrice && maxPrice ? maxPrice : productWithRange?.maxPrice;

    if (displayMin === undefined || displayMax === undefined) {
      return (
        <div className={cn('mt-1 flex gap-[0.4rem]', className)}>
          <span
            className={cn(
              'font-bold',
              textColor === 'white'
                ? 'text-white'
                : `text-brand-${
                    product.on_sale && showSalePrice ? 'secondary' : 'primary'
                  }`,
              textSize === 'large' ? 'text-[2.1rem]' : 'text-[1.6rem]'
            )}
          >
            {formatCurrency(fullPrice)}
          </span>
          {product.on_sale && showSalePrice && (
            <span
              className={cn(
                'font-normal line-through',
                textColor === 'white' ? 'text-white' : 'text-black',
                textSize === 'large' ? 'text-[1.8rem]' : 'text-[1.6rem]'
              )}
            >
              {formatCurrency(product.og_price)}
            </span>
          )}
        </div>
      );
    }

    return (
      <div className={cn('mt-1 flex items-center gap-2', className)}>
        <span
          className={cn(
            'font-bold',
            textColor === 'white' ? 'text-white' : 'text-brand-primary',
            textSize === 'large' ? 'text-[2.1rem]' : 'text-[1.8rem]'
          )}
        >
          {formatCurrency(displayMin)}
        </span>

        <div
          className={cn(
            'h-px w-6',
            textColor === 'white' ? 'bg-white' : 'bg-zinc-600'
          )}
        />

        <span
          className={cn(
            'font-bold',
            textColor === 'white' ? 'text-white' : 'text-brand-primary',
            textSize === 'large' ? 'text-[2.1rem]' : 'text-[1.8rem]'
          )}
        >
          {formatCurrency(displayMax)}
        </span>
      </div>
    );
  }

  return (
    <div className={cn('mt-1 flex items-center gap-2', className)}>
      <span
        className={cn(
          'font-bold',
          textColor === 'white'
            ? 'text-white'
            : `text-brand-${
                product.on_sale && showSalePrice ? 'secondary' : 'primary'
              }`,
          textSize === 'large' ? 'text-[2.1rem]' : 'text-[1.8rem]'
        )}
      >
        {formatCurrency(fullPrice)}
      </span>

      {product.on_sale && showSalePrice && (
        <span
          className={cn(
            'font-normal line-through',
            textColor === 'white' ? 'text-white' : 'text-zinc-700',
            textSize === 'large' ? 'text-[1.8rem]' : 'text-[1.6rem]'
          )}
        >
          {formatCurrency(product.og_price)}
        </span>
      )}
    </div>
  );
};

export default ProductPriceRange;
